import { useEffect, useState } from 'react'
import { CalendarClock, Trash2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useSetVehicleExpiry } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { KIND_LABEL, expiryChip, type ExpiryKind, type ExpiryRow, type ExpiryState } from '../../utils/expiries'

/**
 * Record, change or clear one compliance date for one vehicle.
 *
 * CLEARING IS A FIRST-CLASS ACTION, not an afterthought. A date captured against
 * the wrong car has to be removable by the person who captured it, and a wrong
 * date on a compliance screen is worse than an honest blank: it turns a car
 * nobody has checked into a green tick. The button says what it does.
 *
 * The dialog does not touch the task. vehicle_expiry_raise_tasks_v1 runs every
 * morning and reconciles the board with whatever is recorded here, so the copy
 * says so rather than implying the board changes as you click.
 */
export function ExpiryDateDialog({
  row,
  kind,
  state,
  expiresOn,
  onSaved,
}: {
  row: ExpiryRow
  kind: ExpiryKind
  state: ExpiryState | null
  expiresOn: string | null
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(expiresOn ?? '')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const save = useSetVehicleExpiry()
  const chip = expiryChip(state, expiresOn)

  useEffect(() => {
    if (open) {
      setDate(expiresOn ?? '')
      setReference('')
      setNote('')
    }
  }, [open, expiresOn])

  async function submit(clear: boolean) {
    try {
      await save.trigger({
        vehicle_id: row.vehicle_id,
        kind,
        expires_on: clear ? null : date || null,
        reference: reference.trim() || null,
        note: note.trim() || null,
      }).result
      toast.success(
        clear
          ? `${KIND_LABEL[kind]} date cleared on ${row.plate ?? 'the vehicle'}`
          : `${KIND_LABEL[kind]} recorded on ${row.plate ?? 'the vehicle'}`,
      )
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not save that date', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${KIND_LABEL[kind]} on ${row.plate ?? 'this vehicle'}: ${chip.label}. Click to change.`}
        >
          <Chip tone={chip.tone} className="cursor-pointer hover:opacity-80">
            {chip.label}
          </Chip>
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {KIND_LABEL[kind]}, {row.plate ?? 'vehicle'}
          </DialogTitle>
          <DialogDescription>
            {kind === 'licence_disc'
              ? 'A lapsed disc takes the car off the road and puts the driver at risk of arrest with a fare-paying passenger aboard.'
              : kind === 'insurance'
                ? 'Record the date cover runs to. If the owner insures this vehicle himself, record his date here too: an uninsured car on the road is the same exposure whoever pays the premium.'
                : 'Record the date the current inspection certificate runs to.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`exp-${row.vehicle_id}-${kind}`}>Expires on</Label>
            <Input
              id={`exp-${row.vehicle_id}-${kind}`}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              A task is raised sixty days before this date and lands on the manager who holds the
              car. Recording a renewed date closes the old task by itself.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`ref-${row.vehicle_id}-${kind}`}>
              {kind === 'insurance' ? 'Policy number' : kind === 'inspection' ? 'Certificate number' : 'Reference'}
              <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Input
              id={`ref-${row.vehicle_id}-${kind}`}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="What the paper is identified by"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`note-${row.vehicle_id}-${kind}`}>
              Note <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Textarea
              id={`note-${row.vehicle_id}-${kind}`}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Where the date came from"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="gap-1.5 text-destructive"
            disabled={save.loading || !expiresOn}
            onClick={() => submit(true)}
          >
            <Trash2 className="h-4 w-4" />
            Clear the date
          </Button>
          <Button type="button" disabled={save.loading || !date} onClick={() => submit(false)}>
            {save.loading ? <Spinner className="mr-2 h-4 w-4" /> : <CalendarClock className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
